
import IconCheck from '@mui/icons-material/Check'
import IconDropdown from '@mui/icons-material/KeyboardArrowRight'
import IconError from '@mui/icons-material/WarningRounded'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import Collapse from '@mui/material/Collapse'
import FormControlLabel from '@mui/material/FormControlLabel'
import { SidebarListSelector } from 'components/App/components/AppNavigationSidebar/publishing/SidebarListSelector'
import SidebarProfileSelector from 'components/App/components/AppNavigationSidebar/publishing/SidebarProfileSelector'
import BucketSelect from 'components/BucketSelect'
import { FacebookAlbumSelector } from 'components/Composer/AlbumSelector/FacebookAlbumSelector'
import { PinterestBoardSelector } from 'components/Composer/AlbumSelector/PinterestBoardSelector'
import { NOTIFICATION_DURATION_LONG } from 'components/ConnectedSnackbar'
import { PPDrawer } from 'components/PPDrawer'
import PPSelect from 'components/PPSelect'
import {
  ARTICLE_TYPE,
  PHOTO_TYPE,
  PlannedPost,
  PostType,
  VIDEO_TYPE
} from 'interfaces'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useMountEffect } from 'hooks/useMountEffect'
import { FormattedMessage, useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { of as observableOf, Subject } from 'rxjs'
import { catchError, mergeMap, tap } from 'rxjs/operators'
import { pinterestProfilesBoardsSelector } from 'services/compose/selectors'
import { connectedDestinationsSelector, getAlbums, getPinterestBoards } from 'services/destinations'
import { userListsSelector } from 'services/lists/selectors'
import { bulkDeletePosts } from 'services/post/actions'
import { copyPosts } from 'services/post/copy/actions'
import { CopyPostsData } from 'services/post/copy/net'
import { bucketsSelector } from 'services/post/selectors'
import { message, warning } from 'services/snackbar'
import { GOOGLE_BUTTON_TYPES } from 'shared/constants'
import { FBAlbum, PinterestBoard } from 'shared/types'
import { StoreThunkDispatch } from 'store/state'
import styles from './CopyPostsPopup.pcss'
import { ContentType, BRAND_GOOGLE, List, PostDestination } from 'shared'

const ERROR_MSG_TIMEOUT = 6000

interface CopyPostsPopupProps {
  open: boolean
  posts: PlannedPost[]
  allSelected: boolean
  postType: PostType | 'scheduled' | 'archived'
  types?: ContentType[]
  query?: string
  bucketId?: string
  bucketView?: boolean
  source?: 'bucket' | 'no-bucket'
  onClose: () => void
  onCopyStarted: () => void
  onCopyFinished?: () => void
}

export function CopyPostsPopup(props: CopyPostsPopupProps) {
  const intl = useIntl()
  const dispatch = useDispatch<StoreThunkDispatch>()
  const [profilesView, setProfilesView] = useState<string>('grid')
  const [selectedListId, setSelectedListId] = useState<string | null>(null)
  const [selectedProfileIds, setSelectedProfileIds] = useState<string[]>([])
  const [selectedFBAlbums, setSelectedFBAlbums] = useState<Record<string, FBAlbum | undefined>>({})
  const [selectedPinterestboards, setSelectedPinterestBoards] = useState<Record<string, PinterestBoard>>({})
  const [selectedBucketId, setSelectedBucketId] = useState<string | undefined>(undefined)
  const [selectedBucketProfileIds, setSelectedBucketProfileIds] = useState<string[]>([])
  const [googleButtonType, setGoogleButtonType] = useState<string | undefined>(undefined)
  const [listsExpanded, setListsExpanded] = useState(false)
  const [shouldDeleteOriginal, setShouldDeleteOriginal] = useState(false)
  const [copyToTop, setCopyToTop] = useState(false)
  const profiles = useSelector(connectedDestinationsSelector)
  const lists = useSelector(userListsSelector)
  const buckets = useSelector(bucketsSelector)
  const boards = useSelector(pinterestProfilesBoardsSelector)
  const copyPosts$ = useRef<Subject<CopyPostsData>>(undefined)
  const postIdToPpid = useRef<Record<string, string>>({})

  const resetState = () => {
    setSelectedProfileIds([])
    setSelectedBucketId(undefined)
    setSelectedBucketProfileIds([])
    setShouldDeleteOriginal(false)
    setCopyToTop(false)
    setSelectedPinterestBoards({})
  }

  const closePopup = () => {
    props.onClose()
    resetState()
  }

  useEffect(() => {
    if (props.posts.length > 0) {
      postIdToPpid.current = props.posts.reduce((map, post) => {
        map[post.id] = post.ppPageId
        return map
      }, {} as Record<string, string>)
    }
  }, [props.posts])

  useEffect(() => {
    setShouldDeleteOriginal(false)
    setListsExpanded(false)
  }, [props.open])

  useEffect(() => {
    if (props.posts.length === 1 && props.posts[0].network === BRAND_GOOGLE) {
      setGoogleButtonType(props.posts[0].details?.buttonType)
    }
  }, [props.posts])

  const fetchFBAlbums = useCallback((profile: PostDestination) => {
    return dispatch(getAlbums(profile)).unwrap()
  }, [dispatch])

  const fetchPIBoards = useCallback((profile: PostDestination) => {
    return dispatch(getPinterestBoards({ profile })).unwrap()
  }, [dispatch])

  const onSelectedBucketChange = (id: string | undefined) => {
    setSelectedBucketId(id)
    setSelectedListId(null)
    if (!id) {
      setSelectedBucketProfileIds([])
      return
    }
    const ppids = id && buckets.find(b => b.id === id)?.ppids
    if (ppids) {
      const ids = ppids.map(ppid => Object.values(profiles).find(p => p.ppid === ppid)?.id).filter(Boolean) as string[]
      setSelectedProfileIds(ids)
      setSelectedBucketProfileIds(ids)
    }
  }

  const deletePosts = (ids: string[]) => {
    if (!postIdToPpid.current || props.postType === 'posted') {
      return
    }
    const ppids = ids.map(id => postIdToPpid.current[id])
    dispatch(bulkDeletePosts(ids, ppids, props.postType as any))
      .pipe(
        catchError((res) => {
          dispatch(message('Error deleting posts. Please try again.', 'error'))
          return observableOf({ error: res })
        })
      )
      .subscribe((res: any) => {
        if (res.error) {
          return
        }
        props.onCopyFinished?.()
        resetState()
      })
  }

  useMountEffect(() => {
    copyPosts$.current = new Subject()
    copyPosts$.current
      .pipe(
        mergeMap(data => dispatch(copyPosts(data)).pipe(
          catchError((data) => {
            return observableOf({ error: data.response?.error || data })
          }),
          tap(response => {
            const withDelete = data.deleteOriginal && response.postsOk && response.postsOk.length > 0
            if (withDelete) {
              deletePosts(response.postsOk.map((id: number) => id.toString()))
            }
            return { ...response, withDelete }
          })
        ))
      )
      .subscribe((response: any) => {
        // NOTE: if deleteOriginal is true, onCopyFinished will be called from deletePosts
        if (!response.withDelete && props.onCopyFinished) {
          props.onCopyFinished()
          resetState()
        }

        if (response.countErrors) {
          const msg = (
            <div>
              {response.countOk > 0 && (
                <div className={styles['msg-error']}>
                  <IconCheck className={styles['icon-ok']} />
                  {intl.formatMessage({ id: 'post.notifications.posts-copy-result-success' }, { count: response.countOk })}
                </div>
              )}
              <div className={styles['msg-error']}>
                <IconError className={styles['icon-error']} />
                {intl.formatMessage({ id: 'post.notifications.posts-copy-result-failed' }, { count: response.countErrors })}
              </div>
            </div>
          )
          dispatch(warning(
            msg as any,
            'download report',
            () => { window.open(response.downloadReport, '_self') },
            ERROR_MSG_TIMEOUT
          ))
          return
        }

        if (response.error) {
          if (response.error.message) {
            dispatch(message(response.error.message, 'error', NOTIFICATION_DURATION_LONG))
          } else {
            dispatch(message(intl.formatMessage({ id: 'post.notifications.posts-copy-failed' }), 'error'))
          }
          return
        }

        dispatch(message(intl.formatMessage({ id: 'post.notifications.posts-copied' }), 'success'))
      })
    return () => copyPosts$.current?.unsubscribe()
  })

  const types = useMemo(() => {
    const postTypes: Record<string, boolean> = {}
    for (const post of props.posts) {
      postTypes[post.contentType] = true
    }
    return postTypes
  }, [props.posts])

  const profilesByNetwork = useMemo(() => {
    return selectedProfileIds.reduce((map: Record<string, PostDestination[]>, id: string) => {
      const profile = profiles[id]
      if (map[profile.type]) {
        map[profile.type].push(profile)
      } else {
        map[profile.type] = [profile]
      }
      return map
    }, {})
  }, [selectedProfileIds, profiles])

  const onSelectedListChange = (list: List) => {
    setSelectedListId(list.id)
    setSelectedProfileIds(list.pages.filter(id => Boolean(profiles[id])))
    setSelectedBucketId(undefined)
    setSelectedBucketProfileIds([])
  }

  const onSelectedProfilesChange = (ids: string[]) => {
    setSelectedProfileIds(ids)
    setSelectedListId(null)
  }

  const onFBSelectedAlbumChange = useCallback((profile: PostDestination, album: FBAlbum | undefined) => {
    setSelectedFBAlbums(current => ({
      ...current,
      [profile.id]: album
    }))
  }, [])

  const onSelectedBoardChange = useCallback((ppid: string, board: PinterestBoard) => {
    setSelectedPinterestBoards(current => ({
      ...current,
      [ppid]: board
    }))
  }, [])

  const submit = () => {
    const postIds: string[] = []
    const postPPIds: string[] = []
    for (const post of props.posts) {
      postIds.push(post.id)
      postPPIds.push(post.ppPageId)
    }
    if (!selectedBucketId && selectedProfileIds.length === 0) {
      dispatch(message('Select target Bucket or profiles!', 'warning'))
      return
    }
    copyPosts$.current?.next({
      copyFromPPIds: [...new Set(postPPIds)],
      copyToPPIds: selectedProfileIds.map(id => profiles[id].ppid),
      fbAlbums: Object.values(selectedFBAlbums).filter(Boolean) as FBAlbum[],
      piBoards: Object.values(selectedPinterestboards).filter(Boolean) as PinterestBoard[],
      postIds,
      postType: props.postType,
      all: props.allSelected,
      query: props.query,
      types: props.types,
      bucketId: selectedBucketId,
      fromBucket: props.bucketId || props.bucketView,
      googleButtonType,
      deleteOriginal: shouldDeleteOriginal,
      copyToTop,
      source: props.source
    })
    props.onCopyStarted()
  }

  const visibleProfiles = selectedBucketId
    ? Object.keys(profiles).reduce((map: Record<string, PostDestination>, id: string) => {
      const profile = profiles[id]
      if (selectedBucketProfileIds.includes(profile.id)) {
        map[id] = profile
      }
      return map
    }, {})
    : profiles

  const toggleListsExpanded = () => {
    setListsExpanded(!listsExpanded)
  }

  const toggleDeleteOriginal = () => {
    setShouldDeleteOriginal(!shouldDeleteOriginal)
  }

  const toggleCopyToTop = () => {
    setCopyToTop(!copyToTop)
  }

  const footer = (
    <div className={styles['footer-actions']}>
      <Button className={styles['btn-cancel']} variant="text" onClick={closePopup}>
        <FormattedMessage id="actions.cancel" />
      </Button>
      <Button className={styles['btn-submit']} color="primary" variant="contained" onClick={submit}>
        <FormattedMessage id="post.actions.copy-posts" />
      </Button>
    </div>
  )

  return (
    <PPDrawer
      open={props.open}
      onClose={closePopup}
      size="sm"
      data-testid="copy-posts-popup"
      title={<FormattedMessage id="post.labels.copy-posts-heading" />}
      footer={footer}
    >
      <p className={styles.subtitle}>
        <FormattedMessage id="post.labels.copy-posts-title" />
      </p>
      <div className={styles['buckets-box']}>
        <p><FormattedMessage id="label.generic.buckets" /></p>
        <BucketSelect
          buckets={buckets}
          emptySelectionLabel="No bucket"
          selectedBucketId={selectedBucketId}
          className={styles['buckets-select']}
          onSelectedBucketChange={onSelectedBucketChange}
        />
      </div>
      <div className={styles['profiles-box']}>
        <p><FormattedMessage id="general.labels.profiles" /></p>
        <SidebarProfileSelector
          actionsClassName={styles['actions-profiles']}
          contentClassName={styles['profiles-content']}
          profiles={visibleProfiles}
          activeView={profilesView as any}
          selectedProfileIds={selectedProfileIds}
          multipleProfileSelection
          onSelectedProfilesChange={onSelectedProfilesChange}
          setActiveView={setProfilesView}
        />
      </div>
      <div className={styles['network-settings']}>
        {types[PHOTO_TYPE] && profilesByNetwork.facebook?.length > 0 && profilesByNetwork.facebook.map(profile => (
          <div className={styles['option-box']} key={profile.id}>
            <FacebookAlbumSelector
              profile={profile}
              raised
              fetchAlbums={fetchFBAlbums}
              selectedAlbums={Object.values(selectedFBAlbums).filter(Boolean) as FBAlbum[]}
              onSelectedAlbumChange={onFBSelectedAlbumChange}
            />
          </div>
        ))}
        {profilesByNetwork.pinterest?.length > 0 && profilesByNetwork.pinterest.map(p => {
          const profileBoards = boards[p.ppid]?.length > 0 ? boards[p.ppid] : Object.values(p.pinterestBoards || {})

          return (
            <div key={p.id} className={styles['option-box']}>
              <PinterestBoardSelector
                profile={p}
                boards={profileBoards?.map(b => ({ ...b, selected: b.id === selectedPinterestboards[p.ppid]?.id })) || []}
                raised
                fetchBoards={fetchPIBoards}
                setComposerPinterestBoard={onSelectedBoardChange}
              />
            </div>
          )
        })}
        {profilesByNetwork.google?.length > 0 && props.posts.length === 1 && props.posts[0].network === BRAND_GOOGLE && (
          <div className={styles['option-box']}>
            <PPSelect
              id="google-button-type-select"
              name={intl.formatMessage({ id: 'composer.labels.google.btn-type' })}
              options={GOOGLE_BUTTON_TYPES}
              selectedValue={googleButtonType || 'NONE'}
              className={styles['g-select']}
              popperProps={{ style: { zIndex: '1303' } }}
              onSelectionChange={setGoogleButtonType}
            />
          </div>
        )}
        <div className={styles.errors}>
          {profilesByNetwork.instagram?.length > 0 && types[ARTICLE_TYPE] && (
            <p className={styles.msg}>
              <IconError className={styles.icon} />
              <FormattedMessage id="posts.copy-errors.instagram-article" />
            </p>
          )}
          {profilesByNetwork.google?.length > 0 && types[VIDEO_TYPE] && (
            <p className={styles.msg}>
              <IconError className={styles.icon} />
              <FormattedMessage id="posts.copy-errors.google-video" />
            </p>
          )}
          {profilesByNetwork.bluesky?.length > 0 && types[VIDEO_TYPE] && (
            <p className={styles.msg}>
              <IconError className={styles.icon} />
              <FormattedMessage id="posts.copy-warnings.bluesky-video" />
            </p>
          )}
        </div>
      </div>
      <Collapse in={listsExpanded} timeout="auto" collapsedSize={40}>
        <div className={styles['lists-box']}>
          <p className={styles['lists-toggle']} onClick={toggleListsExpanded}>
            <FormattedMessage id="general.labels.lists" />
            <IconDropdown className={`${styles['btn-toggle']} ${listsExpanded ? styles['btn-flipped'] : ''}`} />
          </p>
          <SidebarListSelector
            selectedList={selectedListId ? lists[selectedListId] : undefined}
            onSelectedListChange={onSelectedListChange}
          />
        </div>
      </Collapse>
      <div className={styles.bottom}>
        {props.postType !== 'posted' && props.postType !== 'archived' && (
          <FormControlLabel
            label="Delete original posts after copy"
            classes={{ label: styles['cb-label'] }}
            control={(
              <Checkbox
                checked={shouldDeleteOriginal}
                color="primary"
                size="small"
                onChange={toggleDeleteOriginal}
              />
            )}
          />
        )}
        <FormControlLabel
          label="Add posts to top of queue"
          classes={{ label: styles['cb-label'] }}
          control={(
            <Checkbox
              checked={copyToTop}
              color="primary"
              size="small"
              onChange={toggleCopyToTop}
            />
          )}
        />
      </div>
    </PPDrawer>
  )
}

export default CopyPostsPopup
